import { useMemo } from 'react';
import { Badge } from '@repletoryreach/sdk-ui';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import type { ConversationMessage } from '@repletoryreach/sdk-api/inbox';
import { useT } from '../../../i18n/I18nProvider';

/** Meta's customer-service window: 24 hours from the last inbound message. */
const WINDOW_MS = 24 * 60 * 60 * 1000;

export interface SessionWindowState {
  /** False once the window has closed, or if the customer has never written. */
  open: boolean;
  /** Milliseconds remaining; 0 once closed. */
  remainingMs: number;
  /** True when the customer has never sent anything, so no window ever opened. */
  neverOpened: boolean;
}

/**
 * Whether free-form replies are still allowed on this conversation.
 *
 * Meta only accepts a free-text reply within 24 hours of the customer's last
 * message; after that only an approved template will send. The console showed
 * nothing about this at all, so an agent could type replies all afternoon and
 * watch every one of them fail for reasons the UI never explained
 * (`inbox.md` §5, "the highest-value feature missing from the page").
 *
 * Derived entirely from messages already on screen: no backend change needed.
 */
export function useSessionWindow(messages: ConversationMessage[], now: number): SessionWindowState {
  return useMemo(() => {
    let lastInbound = 0;
    for (const m of messages) {
      if (m.direction !== 'inbound') continue;
      const at = new Date(m.created_at).getTime();
      if (at > lastInbound) lastInbound = at;
    }
    if (lastInbound === 0) return { open: false, remainingMs: 0, neverOpened: true };

    const remainingMs = Math.max(0, lastInbound + WINDOW_MS - now);
    return { open: remainingMs > 0, remainingMs, neverOpened: false };
  }, [messages, now]);
}

/**
 * The window as a badge in the thread header. Renders nothing until the
 * customer has actually written, because "closed" would be misleading on a
 * conversation that has only ever been outbound.
 */
export function SessionWindowBadge({ window: w }: { window: SessionWindowState }) {
  const t = useT();
  if (w.neverOpened) return null;

  if (!w.open) {
    return (
      <Tooltip label={t('inbox.window.closed.tooltip')} position="bottom">
        <Badge size="sm" color="orange" variant="light" className="rr-inbox2__chip" leftSection={<ClockIcon />}>
          {t('inbox.window.closed')}
        </Badge>
      </Tooltip>
    );
  }

  const hours = Math.floor(w.remainingMs / 3_600_000);
  const minutes = Math.floor((w.remainingMs % 3_600_000) / 60_000);
  const label = hours > 0
    ? t('inbox.window.openHours', { hours, minutes })
    : t('inbox.window.openMinutes', { minutes });

  // Under an hour is worth flagging: it is the last chance to reply freely.
  const runningLow = w.remainingMs < 3_600_000;
  return (
    <Tooltip label={t('inbox.window.open.tooltip')} position="bottom">
      <Badge size="sm" color={runningLow ? 'orange' : 'gray'} variant="light" className="rr-inbox2__chip" leftSection={<ClockIcon />}>
        {label}
      </Badge>
    </Tooltip>
  );
}

export function ClockIcon({ size = 13 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="8" cy="8" r="6" />
      <path d="M8 4.8V8l2.2 1.4" />
    </svg>
  );
}
